import axios from 'axios';

const GITHUB_CONFIG = {
  owner: 'ajeshbs',
  repo: 'cad',
  path: 'Nx/stl',
  imagePath: 'Nx/images',
  branch: 'main'
};

const CACHE_KEY = 'cad_portfolio_stl_files';
const CACHE_DURATION = 1000 * 60 * 30; // 30 minutes

/**
 * Fetch STL files from GitHub repository
 * @returns {Promise<Array>} Array of STL file objects
 */
export const fetchSTLFiles = async () => {
  // Check cache first
  const cached = getCachedData();
  if (cached) {
    return cached;
  }

  try {
    const url = `https://api.github.com/repos/${GITHUB_CONFIG.owner}/${GITHUB_CONFIG.repo}/contents/${GITHUB_CONFIG.path}`;

    const response = await axios.get(url, {
      params: {
        ref: GITHUB_CONFIG.branch
      }
    });

    // Filter only STL files and format the data
    const stlFiles = response.data
      .filter(file => file.name.toLowerCase().endsWith('.stl'))
      .map(file => {
        const name = file.name.replace('.stl', '');
        const imgExt = name === 'Example 33' ? 'png' : 'jpg';
        return {
          name,
          fileName: file.name,
          url: file.download_url,
          thumbnailUrl: `https://raw.githubusercontent.com/${GITHUB_CONFIG.owner}/${GITHUB_CONFIG.repo}/${GITHUB_CONFIG.branch}/${GITHUB_CONFIG.imagePath}/${encodeURIComponent(name)}.${imgExt}`,
          size: file.size,
          sha: file.sha
        };
      })
      .sort((a, b) => {
        // Extract number from filename (e.g., "Example 1" -> 1)
        const numA = parseInt(a.name.match(/\d+/)?.[0] || '0');
        const numB = parseInt(b.name.match(/\d+/)?.[0] || '0');
        return numA - numB;
      });

    // Cache the results
    setCachedData(stlFiles);

    return stlFiles;
  } catch (error) {
    console.error('Error fetching STL files from GitHub:', error);

    // Return fallback data if API fails
    return getFallbackData();
  }
};

/**
 * Get cached data from localStorage
 */
const getCachedData = () => {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) return null;

    const { data, timestamp } = JSON.parse(cached);
    const now = Date.now();

    // Check if cache is still valid
    if (now - timestamp < CACHE_DURATION) {
      return data;
    }

    // Clear expired cache
    localStorage.removeItem(CACHE_KEY);
    return null;
  } catch (error) {
    return null;
  }
};

/**
 * Store data in localStorage cache
 */
const setCachedData = (data) => {
  try {
    const cacheObject = {
      data,
      timestamp: Date.now()
    };
    localStorage.setItem(CACHE_KEY, JSON.stringify(cacheObject));
  } catch (error) {
    console.warn('Failed to cache data:', error);
  }
};

/**
 * Fallback data in case GitHub API fails
 */
const getFallbackData = () => {
  const baseUrl = `https://raw.githubusercontent.com/${GITHUB_CONFIG.owner}/${GITHUB_CONFIG.repo}/${GITHUB_CONFIG.branch}/${GITHUB_CONFIG.path}`;

  // Generate fallback list based on known examples
  const examples = Array.from({ length: 29 }, (_, i) => {
    const num = i + 1;
    // Skip Example 14 as it's not in the STL folder
    if (num === 14) return null;

    const imgExt = num === 33 ? 'png' : 'jpg';
    return {
      name: `Example ${num}`,
      fileName: `Example ${num}.stl`,
      url: `${baseUrl}/Example%20${num}.stl`,
      thumbnailUrl: `https://raw.githubusercontent.com/${GITHUB_CONFIG.owner}/${GITHUB_CONFIG.repo}/${GITHUB_CONFIG.branch}/${GITHUB_CONFIG.imagePath}/Example%20${num}.${imgExt}`,
      size: 0,
      sha: ''
    };
  }).filter(Boolean);

  return examples;
};

/**
 * Clear the cache manually
 */
export const clearCache = () => {
  localStorage.removeItem(CACHE_KEY);
};
